import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import Decimal from 'decimal.js';

import { useCreatePaymentPlan } from '../../../lib/hooks/use-create-payment-plan';
import { useCalculateBookingDetails } from '../../../lib/hooks/use-post-calculate-booking-details';
import { useUrlParams } from '../../../utils/use-url-params';
import { ErrorMessage } from '../../../shared/enum/ErrorMessage';
import { CreatePaymentPlanDto } from '../../../api/paymentPlanService/dto/CreatePaymentPlanDto';
import { CreateBasketDto } from '../../../api/basketService/dto/CreateBasket.dto';
import { useCreateBasket } from '../../../lib/hooks/use-post-create-basket';

export const useProcessBrochureCallback = () => {
    const [searchParams] = useSearchParams();
    const urlParams = useUrlParams();
    const { mutateAsync: createPaymentPlan } = useCreatePaymentPlan();
    const { mutateAsync: fetchBookingDetails } = useCalculateBookingDetails();
    const { mutateAsync: createBasketAsync } = useCreateBasket();

    const LEAD_SOURCE_DIRECT = 'direct';
    const LEAD_MEDIUM = '-';
    const LEAD_CAMPAIGN = '-';
    const VENDOR_STUDENT_BOOKING = 'studentbookingvendor';
    const fillerWeekEligible = searchParams.get('fillerWeekEligible');
    const instalmentId = searchParams.get('instalmentId');
    const isRebooker = searchParams.get('isRebooker');
    const promotionId = searchParams.get('pid') || searchParams.get('promotionId');

    const selectedStudents = searchParams.get('students');

    return useQuery({
        queryKey: ['brochure-callback'],
        queryFn: async () => {
            if (!urlParams.roomCode || !urlParams.academicYear)
                throw new Error(ErrorMessage.NO_ROOM_PROVIDED);

            const calculatedBookingData = await fetchBookingDetails({
                roomName: urlParams.roomCode,
                academicYear: urlParams.academicYear,
            });

            const { room, building, bookingDetails } = calculatedBookingData;

            const foundTermAndOption = bookingDetails.terms
                .map((term) => ({
                    term,
                    instalment: Object.values(term.instalments).find(
                        (instalment) => instalment.id === instalmentId
                    ),
                }))
                .find((x) => Boolean(x.instalment));

            if (!foundTermAndOption?.instalment)
                throw new Error(ErrorMessage.NO_USER_CHOICES_PROVIDED);

            const { term: selectedTerm, instalment: selectedInstalment } = foundTermAndOption;

            const payload = new CreatePaymentPlanDto({
                academicYear: urlParams.academicYear,
                basePrice: room.base_price,
                fillerWeekEligible: !!fillerWeekEligible,
                instalmentId,
                instalmentType: selectedInstalment.name,
                isRebooker: !!isRebooker,
                promotionId,
                roomCode: urlParams.roomCode,
                selectedStudents: selectedStudents ? Number(selectedStudents) : 1,
                selectedTerm: selectedTerm.name,
            });

            const createdPaymentPlan = await createPaymentPlan(payload);

            const createBasketDto = new CreateBasketDto({
                user: null,
                products: [
                    {
                        id: room.id,
                        room_code: room.room_code,
                        academic_year: room.academic_year,
                        building_id: room.building_id,
                        development_id: building.development_id,
                        price: room.monthly_price ? room.monthly_price : room.base_price,
                        currency: room.currency,
                        deposit: new Decimal(0),
                        advance_payment_weeks: room.advance_payment_weeks,
                        deposit_prepaid: room.deposit_prepaid,
                        selected_students: selectedStudents ? parseInt(selectedStudents, 10) : 1,
                        term: selectedTerm.name,
                        beds: room.number_of_beds,
                        payment_plan_id: instalmentId,
                        instalment_type: selectedInstalment.name,
                        cbl: false,
                        stl: false,
                        occupants: [],
                        selected_price: {
                            fee: createdPaymentPlan.weeklyPrice,
                            today: createdPaymentPlan.paymentOptions[0].price,
                            non_discounted: createdPaymentPlan.subtotal,
                        },
                        legal_representative: null,
                        promotion_id: createdPaymentPlan.promotion?.id ?? null,
                    },
                ],
                options: {
                    rebooker: false,
                    rebookerPhase: null,
                    fillerWeekEligible: false,
                    agentCode: urlParams.agentCode,
                    agentStudentId: urlParams.agentStudentId,
                    referrerCode: null,
                },
                tracking: {
                    lead_source: LEAD_SOURCE_DIRECT,
                    lead_medium: LEAD_MEDIUM,
                    lead_campaign: LEAD_CAMPAIGN,
                },
                vendor: VENDOR_STUDENT_BOOKING,
                room_mates: null,
                payment_options_snapshot: calculatedBookingData.bookingDetails.terms,
            });

            const basket = await createBasketAsync(createBasketDto);

            return basket;
        },
    });
};
